import React, { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import ControlAlertMock from "./ControlAlertMock";
import {
  CONTROL_ACTIONS,
  CONTROL_HEADLINE,
  CONTROL_PAYOFF,
  CONTROL_SCAN_LINE,
  CONTROL_STAMP,
  CONTROL_SUBHEAD,
} from "./controlContent";

const ControlSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const reduceMotion = useReducedMotion();

  return (
    <section
      className="relative py-14 sm:py-16 lg:py-20 px-6 bg-[#0a0c0f] overflow-hidden"
      data-testid="control-section"
      ref={ref}
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_80%_50%,rgba(249,115,22,0.06),transparent_65%)] pointer-events-none" />
      <div className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-500/10 to-transparent pointer-events-none" />

      <div className="max-w-6xl mx-auto relative">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="badge-mono text-[10px] font-bold tracking-[0.28em] text-orange-400/90 uppercase">
            {CONTROL_STAMP}
          </span>
        </div>

        <p
          className="text-sm sm:text-base font-mono text-gray-500 mb-3 tracking-wide"
          data-testid="control-scan-line"
        >
          {CONTROL_SCAN_LINE}
        </p>

        <h2
          className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-[#F3F4F6] leading-[1.1] mb-4 max-w-3xl"
          data-testid="control-headline"
        >
          {CONTROL_HEADLINE.lead}{" "}
          <span className="text-orange-400">{CONTROL_HEADLINE.accent}</span>
        </h2>

        <p className="font-body text-sm sm:text-base text-[#9CA3AF] mb-10 max-w-2xl leading-relaxed">
          {CONTROL_SUBHEAD}
        </p>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <ControlAlertMock />
            <p className="mt-4 text-center lg:text-left text-[10px] font-mono text-gray-600 tracking-wide">
              Live notification · Direct dashboard link
            </p>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="relative pl-1" data-testid="control-action-ladder">
              <div className="absolute left-[11px] top-4 bottom-4 w-px bg-gradient-to-b from-orange-500/40 via-orange-500/15 to-teal-500/30" />

              <div className="space-y-4 sm:space-y-5">
                {CONTROL_ACTIONS.map((action, index) => {
                  const isHero = action.highlight;

                  return (
                    <motion.div
                      key={action.id}
                      initial={reduceMotion ? false : { opacity: 0, x: 12 }}
                      animate={
                        reduceMotion || isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 12 }
                      }
                      transition={{ duration: 0.35, delay: index * 0.1 }}
                      className={`relative flex gap-4 sm:gap-5 ${
                        isHero ? "sm:-mr-1" : ""
                      }`}
                      data-testid={`control-card-${index}`}
                    >
                      <div
                        className={`relative z-10 flex-shrink-0 w-6 h-6 rounded-full border-2 mt-1 ${
                          isHero
                            ? "border-orange-400 bg-orange-500/30 shadow-[0_0_14px_rgba(249,115,22,0.5)]"
                            : "border-white/20 bg-[#0a0c0f]"
                        }`}
                      />

                      <div
                        className={`flex-1 min-w-0 rounded-xl border px-4 py-4 sm:px-5 sm:py-4 ${
                          isHero
                            ? "border-orange-500/40 bg-gradient-to-r from-orange-500/12 to-transparent"
                            : "border-white/[0.07] bg-white/[0.015]"
                        }`}
                      >
                        {isHero && (
                          <span className="badge-mono text-[9px] text-orange-400 tracking-wider mb-1.5 block">
                            KEY CONTROL
                          </span>
                        )}

                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1">
                          <span className="badge-mono text-[10px] text-gray-600">{action.step}</span>
                          <span
                            className={`font-mono font-bold text-xl sm:text-2xl leading-none ${
                              isHero ? "text-orange-400" : "text-teal-400/90"
                            }`}
                          >
                            {action.verb}
                          </span>
                          <span className="font-heading font-semibold text-sm text-white">
                            {action.label}
                          </span>
                        </div>
                        <p className="text-[11px] sm:text-xs text-gray-500 font-body leading-relaxed">
                          {action.detail}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            <p
              className="mt-8 pl-7 text-sm sm:text-base text-gray-400 font-body leading-relaxed"
              data-testid="control-payoff"
            >
              <span className="text-white font-semibold">{CONTROL_PAYOFF}</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ControlSection;
